import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import type { WarehouseRecountShelfSublocation } from "@/lib/warehouseRecountApi";
import {
  WarehouseRecountShelfField,
  warehouseRecountShelfDraftToStorageKey,
} from "@/pages/warehouse/WarehouseRecountShelfField";

import { printInboundInventoryLocationLabel } from "./inboundApi";
import { useInboundZplPrinter } from "./inboundZplPrinter";

export function InboundToStockPutAwayModal({
  open,
  sku,
  sublocationHints,
  hintsAreLastShelf = false,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  sku: string;
  sublocationHints: string[];
  hintsAreLastShelf?: boolean;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (sublocation: string) => void;
}): JSX.Element | null {
  const { showToast } = useHubToast();
  const { station, setStation, selectedPrinter, unavailableMessage, documents, printersQuery } = useInboundZplPrinter();
  const [sublocation, setSublocation] = useState("");

  useEffect(() => {
    if (open) {
      setSublocation("");
    }
  }, [open, sku]);

  const printLocationMutation = useMutation({
    mutationFn: () => {
      const printer = selectedPrinter.trim();
      if (sku.trim() === "") {
        throw new Error("SKU is required to print a location label.");
      }
      if (printer === "") {
        throw new Error("Pick a 4x2 label printer first.");
      }
      const selectedPrinterRow = printersQuery.data?.printers.find((row) => row.name === printer);
      const stockSize = selectedPrinterRow?.labelSize?.trim();
      if (stockSize != null && stockSize !== "" && stockSize !== "4x2") {
        throw new Error(`Selected printer is ${stockSize} stock. Location labels are 4x2.`);
      }
      return printInboundInventoryLocationLabel({ sku, printer });
    },
    onSuccess: (result) => {
      showToast({
        message: `Location label for ${result.sku} sent to the printer.`,
        tone: "success",
      });
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  if (!open) return null;

  const shelfKey = warehouseRecountShelfDraftToStorageKey(sublocation);
  const canConfirm = shelfKey !== "";
  const shelfSublocations: WarehouseRecountShelfSublocation[] = sublocationHints.map((hint) => ({
    sublocation: hint,
    label: hint,
    hub_quantity: 0,
    labeled_quantity: 0,
    carton_count: 0,
    last_known: hintsAreLastShelf,
  }));

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <Card className="mt-2 w-full max-w-lg shadow-xl">
        <CardHeader className="border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Put away {sku}</h2>
        </CardHeader>
        <CardBody className="space-y-4">
          <WarehouseRecountShelfField
            id="to-stock-sublocation"
            shelfDraft={sublocation}
            shelfSublocations={shelfSublocations}
            showList={shelfSublocations.length > 0}
            disabled={pending}
            onShelfDraftChange={setSublocation}
            onCommit={setSublocation}
          />
          <div>
            <ZplLocationSelect
              id="to-stock-location-label-printer"
              size="touch"
              value={station}
              onChange={setStation}
              unavailableMessage={unavailableMessage}
              disabled={pending || printLocationMutation.isPending}
            
      documents={documents}
    />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" disabled={pending} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={sku.trim() === "" || selectedPrinter.trim() === "" || pending || printLocationMutation.isPending}
              onClick={() => printLocationMutation.mutate()}
            >
              {printLocationMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Print location label
            </Button>
            <Button
              type="button"
              disabled={!canConfirm || pending}
              onClick={() => onConfirm(shelfKey)}
            >
              Confirm put away
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}
